import { useMemo } from 'react';
import AddUserEmailLookupForm from 'forms/add-user-email-lookup-form/add-user-email-lookup-form';
import AddUserDetailsForm from 'forms/add-user-details-form/add-user-details-form';
import AdminAddUserAssignAccounts from 'forms/admin-add-user-assign-accounts/admin-add-user-assign-accounts';
import AddUserConfirmationForm from 'forms/add-user-confirmation-form/add-user-confirmation-form';
import CustomerAssignUserAccounts from 'features/users/components/customer-assign-user-accounts/customer-assign-user-accounts';

/**
 * Custom hook to configure Add User Modal steps and action buttons.
 * Encapsulates step definitions and button configurations for each step.
 *
 * @param {Object} params - Configuration parameters
 * @param {Object} params.steps - Step enum constants
 * @param {number} params.stepId - Current step ID
 * @param {string} params.emailLookupFormId - Form ID for email lookup
 * @param {string} params.addUserDetailsFormId - Form ID for user details
 * @param {string} params.adminAddUserAssignAccountsFormId - Form ID for admin accounts
 * @param {string} params.email - User email
 * @param {Function} params.setEmail - Function to update email
 * @param {Function} params.resetUserDetails - Function to reset user details
 * @param {Function} params.setFormId - Function to update form ID
 * @param {Object} params.userDetails - User details data
 * @param {Function} params.handleAddUserDetailsSubmit - Handler for user details submission
 * @param {Function} params.handleAdminAssignAccountsSubmit - Handler for admin accounts submission
 * @param {Function} params.setAdminAssignAccountsSubmitButtonDisabled - Function to set admin button state
 * @param {Function} params.handleCustomerAddUser - Handler for customer user creation
 * @param {boolean} params.canReadWrite - Whether user has read/write permissions
 * @param {Function} params.setStepId - Function to update current step
 * @param {string} params.roleId - Selected role ID
 * @param {boolean} params.userEmailLookupLoading - Whether email lookup is loading
 * @param {boolean} params.addUserDetailsNextButtonDisabled - Whether next button is disabled
 * @param {boolean} params.createUserLoading - Whether user creation is loading
 * @param {boolean} params.createUserLoaded - Whether user creation completed
 * @param {boolean} params.adminAssignAccountsSubmitButtonDisabled - Whether admin submit is disabled
 * @param {Object} params.primaryActionButton - Primary action button from store
 * @param {Object} params.secondaryActionButton - Secondary action button from store
 * @param {Function} params.closeModal - Function to close modal
 *
 * @returns {Object} Configuration
 * @returns {Array} steps - Array of step configurations
 * @returns {Object} currentStep - Current step configuration
 * @returns {Function} getActionButtons - Function to get action buttons for current step
 */
export const useAddUserModalConfig = ({
  steps,
  stepId,
  emailLookupFormId,
  addUserDetailsFormId,
  adminAddUserAssignAccountsFormId,
  email,
  setEmail,
  resetUserDetails,
  setFormId,
  userDetails,
  handleAddUserDetailsSubmit,
  handleAdminAssignAccountsSubmit,
  setAdminAssignAccountsSubmitButtonDisabled,
  handleCustomerAddUser,
  canReadWrite,
  setStepId,
  roleId,
  userEmailLookupLoading,
  addUserDetailsNextButtonDisabled,
  createUserLoading,
  createUserLoaded,
  adminAssignAccountsSubmitButtonDisabled,
  primaryActionButton,
  secondaryActionButton,
  closeModal,
}) => {
  // Memoize step configurations
  const addNewUserSteps = useMemo(
    () => [
      {
        stepId: steps.EmailLookup,
        title: 'Add user details',
        subtitle: 'New User',
        component: (
          <div className={'add-user-details'}>
            <AddUserEmailLookupForm
              formId={emailLookupFormId}
              email={email}
              updateEmail={setEmail}
              resetUserDetails={resetUserDetails}
              resetFormId={() => setFormId(emailLookupFormId)}
            />
            <AddUserDetailsForm
              formId={addUserDetailsFormId}
              onSubmit={handleAddUserDetailsSubmit}
              userDetails={userDetails}
            />
          </div>
        ),
      },
      {
        stepId: steps.AdminAssignAccounts,
        title: 'Assign portal accounts',
        subtitle: 'New User',
        component: (
          <AdminAddUserAssignAccounts
            formId={adminAddUserAssignAccountsFormId}
            onSubmit={handleAdminAssignAccountsSubmit}
            setAdminAssignAccountsSubmitButtonDisabled={setAdminAssignAccountsSubmitButtonDisabled}
          />
        ),
        onBackClick: () => {
          setFormId(addUserDetailsFormId);
          setStepId(steps.EmailLookup);
        },
      },
      {
        stepId: steps.CustomerPortalAssignAccounts,
        title: 'Assign accounts',
        subtitle: `${userDetails?.firstName} ${userDetails?.lastName}`,
        component: (
          <CustomerAssignUserAccounts
            onBackClick={() => {
              setFormId(addUserDetailsFormId);
              setStepId(steps.EmailLookup);
            }}
            onSubmit={handleCustomerAddUser}
            canReadWrite={canReadWrite}
          />
        ),
      },
      {
        stepId: steps.AddUserConfirmation,
        title: `${userDetails?.firstName} ${userDetails?.lastName}`,
        subtitle: 'User',
        component: <AddUserConfirmationForm emailId={email} roleId={roleId} />,
        onNextClick: () => {
          setStepId(steps.CustomerPortalAssignAccounts);
        },
      },
    ],
    [
      steps,
      emailLookupFormId,
      email,
      setEmail,
      resetUserDetails,
      setFormId,
      addUserDetailsFormId,
      userDetails,
      handleAddUserDetailsSubmit,
      adminAddUserAssignAccountsFormId,
      handleAdminAssignAccountsSubmit,
      setAdminAssignAccountsSubmitButtonDisabled,
      handleCustomerAddUser,
      canReadWrite,
      setStepId,
      roleId,
    ]
  );

  // Find current step
  const currentStep = useMemo(() => addNewUserSteps.find((step) => step.stepId === stepId), [addNewUserSteps, stepId]);

  // Memoize action buttons configuration generator
  const getActionButtons = useMemo(
    () => () => {
      return [
        ...(currentStep.stepId === steps.EmailLookup
          ? [
              {
                label: 'Next',
                type: userEmailLookupLoading || addUserDetailsNextButtonDisabled ? 'button' : 'submit',
                inactive: userEmailLookupLoading || addUserDetailsNextButtonDisabled,
                onClick: undefined,
              },
              {
                label: 'Cancel',
                onClick: closeModal,
                color: 'black',
              },
            ]
          : []),
        ...(currentStep.stepId === steps.AdminAssignAccounts
          ? [
              {
                label: 'Submit',
                type: createUserLoading || createUserLoaded ? 'button' : 'submit',
                inactive: adminAssignAccountsSubmitButtonDisabled || createUserLoading || createUserLoaded,
              },
              {
                label: 'Back',
                onClick: currentStep.onBackClick,
                color: 'black',
                inactive: createUserLoading || createUserLoaded,
              },
              {
                label: 'Cancel',
                onClick: closeModal,
                color: 'black',
              },
            ]
          : []),
        ...(currentStep.stepId === steps.AddUserConfirmation
          ? [
              {
                label: 'Close',
                onClick: closeModal,
              },
            ]
          : []),
        ...(currentStep.stepId === steps.CustomerPortalAssignAccounts
          ? [
              ...(primaryActionButton ? [primaryActionButton] : []),
              ...(secondaryActionButton
                ? [{ ...secondaryActionButton, onClick: secondaryActionButton.onClick || closeModal }]
                : []),
            ]
          : []),
      ];
    },
    [
      currentStep,
      steps,
      userEmailLookupLoading,
      addUserDetailsNextButtonDisabled,
      closeModal,
      createUserLoading,
      createUserLoaded,
      adminAssignAccountsSubmitButtonDisabled,
      primaryActionButton,
      secondaryActionButton,
    ]
  );

  return {
    addNewUserSteps,
    currentStep,
    getActionButtons,
  };
};
